import { apiClient } from '@/lib/api'
import type {
  CatalogPart,
  CatalogPartSearchResult,
  CatalogPartWriteBody,
  CollectionRun,
  ImportReport,
  PurchaseOrder,
  PurchaseOrderLogisticsBody,
  PurchaseOrderStatus,
  PurchaseOrderWriteBody,
  Supplier,
  SupplierWriteBody,
  WorkOrderPart,
  WorkOrderPartWriteBody,
} from '@/types/parts'

// --- Catalog (price book) ---

export const fetchCatalogParts = async (filters?: {
  q?: string
  manufacturer?: string
  supplier_id?: string
  limit?: number
  offset?: number
}): Promise<CatalogPartSearchResult> => {
  return apiClient<CatalogPartSearchResult>('/parts/catalog', { params: filters })
}

export const fetchCatalogPart = async (id: string): Promise<CatalogPart> => {
  return apiClient<CatalogPart>(`/parts/catalog/${id}`)
}

export const createCatalogPart = async (body: CatalogPartWriteBody): Promise<CatalogPart> => {
  return apiClient<CatalogPart>('/parts/catalog', { method: 'POST', body })
}

export const updateCatalogPart = async (
  id: string,
  body: Partial<CatalogPartWriteBody>,
): Promise<CatalogPart> => {
  return apiClient<CatalogPart>(`/parts/catalog/${id}`, { method: 'PUT', body })
}

export const deleteCatalogPart = async (id: string): Promise<void> => {
  return apiClient<void>(`/parts/catalog/${id}`, { method: 'DELETE' })
}

export const importCatalogCSV = async (
  file: File,
  opts?: { supplier_id?: string; manufacturer?: string },
): Promise<ImportReport> => {
  const form = new FormData()
  form.append('file', file)
  if (opts?.supplier_id) form.append('supplier_id', opts.supplier_id)
  if (opts?.manufacturer) form.append('manufacturer', opts.manufacturer)
  return apiClient<ImportReport>('/parts/catalog/import', { method: 'POST', body: form })
}

// --- Suppliers ---

export const fetchSuppliers = async (includeInactive = false): Promise<Supplier[]> => {
  return apiClient<Supplier[]>('/parts/suppliers', {
    params: { include_inactive: includeInactive || undefined },
  })
}

export const createSupplier = async (body: SupplierWriteBody): Promise<Supplier> => {
  return apiClient<Supplier>('/parts/suppliers', { method: 'POST', body })
}

export const updateSupplier = async (
  id: string,
  body: Partial<SupplierWriteBody>,
): Promise<Supplier> => {
  return apiClient<Supplier>(`/parts/suppliers/${id}`, { method: 'PUT', body })
}

export const deleteSupplier = async (id: string): Promise<void> => {
  return apiClient<void>(`/parts/suppliers/${id}`, { method: 'DELETE' })
}

// --- Work order part lines ---

export const fetchWorkOrderParts = async (workOrderId: string): Promise<WorkOrderPart[]> => {
  return apiClient<WorkOrderPart[]>(`/parts/work-orders/${workOrderId}/parts`)
}

export const addWorkOrderPart = async (
  workOrderId: string,
  body: WorkOrderPartWriteBody,
): Promise<WorkOrderPart> => {
  return apiClient<WorkOrderPart>(`/parts/work-orders/${workOrderId}/parts`, {
    method: 'POST',
    body,
  })
}

export const updateWorkOrderPart = async (
  lineId: string,
  body: Partial<WorkOrderPartWriteBody>,
): Promise<WorkOrderPart> => {
  return apiClient<WorkOrderPart>(`/parts/work-order-parts/${lineId}`, { method: 'PUT', body })
}

export const deleteWorkOrderPart = async (lineId: string): Promise<void> => {
  return apiClient<void>(`/parts/work-order-parts/${lineId}`, { method: 'DELETE' })
}

// --- Purchase orders ---

export const fetchPurchaseOrders = async (filters?: {
  supplier_id?: string
  work_order_id?: string
  status?: PurchaseOrderStatus
}): Promise<PurchaseOrder[]> => {
  return apiClient<PurchaseOrder[]>('/parts/purchase-orders', { params: filters })
}

export const fetchPurchaseOrder = async (id: string): Promise<PurchaseOrder> => {
  return apiClient<PurchaseOrder>(`/parts/purchase-orders/${id}`)
}

export const createPurchaseOrder = async (body: PurchaseOrderWriteBody): Promise<PurchaseOrder> => {
  return apiClient<PurchaseOrder>('/parts/purchase-orders', { method: 'POST', body })
}

export const sendPurchaseOrder = async (id: string, shipTo?: string): Promise<PurchaseOrder> => {
  return apiClient<PurchaseOrder>(`/parts/purchase-orders/${id}/send`, {
    method: 'POST',
    body: { ship_to: shipTo || undefined },
  })
}

export const changePOStatus = async (
  id: string,
  status: PurchaseOrderStatus,
): Promise<PurchaseOrder> => {
  return apiClient<PurchaseOrder>(`/parts/purchase-orders/${id}/status`, {
    method: 'PATCH',
    body: { status },
  })
}

export const updatePOLogistics = async (
  id: string,
  body: PurchaseOrderLogisticsBody,
): Promise<PurchaseOrder> => {
  return apiClient<PurchaseOrder>(`/parts/purchase-orders/${id}`, { method: 'PATCH', body })
}

export const deletePurchaseOrder = async (id: string): Promise<void> => {
  return apiClient<void>(`/parts/purchase-orders/${id}`, { method: 'DELETE' })
}

// --- Price collection ---

export const fetchCollectionRuns = async (supplierId?: string): Promise<CollectionRun[]> => {
  return apiClient<CollectionRun[]>('/parts/collection-runs', {
    params: { supplier_id: supplierId || undefined },
  })
}

export const runCollection = async (supplierId?: string): Promise<CollectionRun> => {
  return apiClient<CollectionRun>('/parts/collection-runs/run', {
    method: 'POST',
    body: supplierId ? { supplier_id: supplierId } : {},
  })
}
